<template>
  <div class="editor-header-grid p-4 pb-0 text-gray-600 text-xs">
    <button
      v-if="isEditMode && !isGridView"
      class="appearance-none bg-transparent p-0 flex uppercase items-center leading-normal place-self-start h-6 gap-1"
      type="button"
      :disabled="!canLeaveEdit"
      @click="$emit('leave-edit-scene')"
    >
      <ChevronLeftIcon class="w-4 h-4" />
      <div class="max-w-38 truncate">{{ deck.title }}</div>
    </button>
    <div v-else class="flex gap-2 uppercase items-center leading-normal">
      <a href="/admin/decks" class="flex items-center">
        <ChevronLeftIcon class="w-4 h-4 mr-1" />
        All 3D Presentations
      </a>
    </div>
    <div v-if="!isEditMode && deck.updated_at" class="flex justify-center items-center gap-4">
      <p class="italic mb-0" data-testid="deck-editor-last-updated">
        <template v-if="isSaving">Saving&hellip;</template>
        <template v-else>Last updated {{ formatDateTimeMed(deck.updated_at) }}</template>
      </p>
    </div>
    <div v-if="!isEditMode" class="flex items-center justify-end gap-3 uppercase font-medium text-2xs text-center">
      <label v-if="deck.type_tags.length" class="text-white rounded px-2 py-1 mb-0 bg-black" type="badge" data-testid="deck-type-tag">
        {{ deck.type_tags[0] }}
      </label>
      <VPermissionsBadge access-type="private" class="normal-case !flex items-center gap-1">
        <AccessTypeIcon :access-type="deck.base_access" class="w-3 h-3" />
        <span>{{ getPermissionsBadgeAccessType(deck.base_access, deck) }}</span>
      </VPermissionsBadge>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { formatDateTimeMed } from '@/lib/datetime-format-utils'
import type { PropType } from 'vue'
import type { EditDeck } from '~/composables/useEditDeck'
import { ChevronLeftIcon } from '@heroicons/vue/24/outline'
import { DeckBaseAccess } from '~/entities/deck'

defineProps({
  deck: {
    type: Object as PropType<EditDeck>,
    required: true,
  },
  isEditMode: {
    type: Boolean,
    default: false,
  },
  isGridView: {
    type: Boolean,
    default: false,
  },
  canLeaveEdit: {
    type: Boolean,
    default: true,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
})

defineEmits(['leave-edit-scene'])

function getPermissionsBadgeAccessType(accessType: DeckBaseAccess, deck: EditDeck) {
  switch (accessType) {
    case DeckBaseAccess.CompanyView:
    case DeckBaseAccess.CompanyEdit:
      return 'Company Users'
    case DeckBaseAccess.Private:
      return 'Only people invited'
    default:
      return 'Anyone with the link'
  }
}
</script>

<style lang="stylus" scoped>
a
  color inherit
</style>
